import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import gradeService from "../../services/gradeService";
import semesterService from "../../services/semesterService";
import classService from "../../services/classService";
import enrollmentService from "../../services/enrollmentService";
import reportService from "../../services/reportService";

// Import hooks
import { useSemester } from "../../hooks";
import { getCurrentSemester } from "../../utils/dateUtils";
import { roundToTwoDecimals } from "../../utils/mathUtils";

// Import all the new components
import ClassSelection from "./components/ClassSelection";
import SemesterTabs from "./components/SemesterTabs";
import SubjectsGrid from "./components/SubjectsGrid";
import AddGradeModal from "./components/modals/AddGradeModal";
import EditGradeModal from "./components/modals/EditGradeModal";
import DeleteGradeModal from "./components/modals/DeleteGradeModal";
import SubjectSelectionModal from "./components/modals/SubjectSelectionModal";
import RemoveSubjectModal from "./components/modals/RemoveSubjectModal";
import GradeTooltip from "./components/GradeTooltip";
import GradesChart from "./GradesChart";
import AnalyticsDashboard from "./AnalyticsDashboard";
import ReportsList from "./ReportsList";
import CurrentSemesterBadge, { SemesterTimeline } from "../common/CurrentSemesterBadge";
import LoadingSpinner from "../common/LoadingSpinner";
import SemesterConfirmationCard from "./components/SemesterConfirmationCard";

const getDefaultSubjectFilters = (professionType) => {
  const normalizedProfession = (professionType || "").toUpperCase();
  const mappedType = {
    INFORMATIK: "IT",
    MEDIAMATIK: "MEDI",
    KV: "KV_FACH",
    KUNDENDIALOG: "KUNDENDIALOG_FACH",
    DETAILHANDEL: "DETAILHANDEL_FACH",
  }[normalizedProfession];

  return Array.from(new Set(["CORE", mappedType].filter(Boolean)));
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (error?.message) return error.message;
  return fallback;
};

const getConfirmationKey = (classId, semesterId, learnerId) => `${classId ?? "legacy"}:${semesterId ?? "legacy"}:${learnerId ?? "legacy"}`;

const StudentGrades = () => {
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkApplied = useRef(false);
  // const { isMobile } = useResponsive();

  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [grades, setGrades] = useState([]);
  const [allGrades, setAllGrades] = useState([]); // Store all grades across all semesters
  const [allSubjects, setAllSubjects] = useState([]); // Store all subjects
  const [userClasses, setUserClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  // Use semester helper hook for the selected class
  const { 
    currentSemester, 
    allSemesters: calculatedSemesters
  } = useSemester(selectedClass?.startDate, selectedClass?.professionType);

  // Add Grade Modal States
  const [showAddGradeModal, setShowAddGradeModal] = useState(false);
  const [selectedSubjectForGrade, setSelectedSubjectForGrade] = useState(null);
  const [newGradeData, setNewGradeData] = useState({
    gradeValue: "",
    weight: 1.0,
    description: "",
    examDate: new Date().toISOString().split("T")[0],
  });

  // Subject Selection Modal States
  const [showSubjectSelectionModal, setShowSubjectSelectionModal] =
    useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [subjectFilters, setSubjectFilters] = useState([]);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState("");

  // Remove Subject Modal States
  const [showRemoveSubjectModal, setShowRemoveSubjectModal] = useState(false);
  const [subjectToRemove, setSubjectToRemove] = useState(null);

  // Edit Grade Modal States
  const [showEditGradeModal, setShowEditGradeModal] = useState(false);
  const [gradeToEdit, setGradeToEdit] = useState(null);
  const [editGradeData, setEditGradeData] = useState({
    gradeValue: "",
    weight: 1.0,
    description: "",
    examDate: "",
  });

  // Delete Grade Modal States
  const [showDeleteGradeModal, setShowDeleteGradeModal] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState(null);

  // Tooltip State
  const [hoveredGrade, setHoveredGrade] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Analytics View State
  const [activeTab, setActiveTab] = useState("overview"); // overview, reports, analytics
  const [semesterConfirmations, setSemesterConfirmations] = useState({});
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationSuccess, setConfirmationSuccess] = useState("");
  const [semesterDefinitiveRows, setSemesterDefinitiveRows] = useState([]);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (!user) {
        setLoading(false);
        return;
      }

      // Load user's classes and semesters
      const [myClassesResponse, semestersResponse] = await Promise.all([
        classService.getMyClasses().catch((error) => {
          console.error("Error loading my classes:", error);
          return { content: [] }; // Backend returns paginated response
        }),
        semesterService.getAllSemesters().catch((error) => {
          console.error("Error loading semesters:", error);
          return [];
        }),
      ]);

      // Handle paginated response from backend
      const classes = myClassesResponse?.content || myClassesResponse || [];
      setUserClasses(classes);
      setSemesters(semestersResponse || []);
    } catch (error) {
      console.error("Error loading initial data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError(
          "Authentication failed. Please refresh the page and try again."
        );
      } else {
        setError(`Failed to load data: ${error.message || "Unknown error"}`);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Helper functions to filter data by selected class
  const getSemestersByClass = useCallback((classItem) => {
    if (!classItem) return [];

    const classSemesters = semesters.filter(
      (semester) =>
        semester.classId === classItem.id ||
        semester.class_id === classItem.id ||
        (!semester.classId &&
          !semester.class_id &&
          (semester.schoolId === classItem.schoolId ||
            semester.school_id === classItem.schoolId)) ||
        (!semester.classId &&
          !semester.class_id &&
          !semester.schoolId &&
          !semester.school_id)
    );

    return classSemesters.slice(0, 10);
  }, [semesters]);

  // Class selection handler
  const handleClassSelect = (classItem) => {
    // If clicking the already selected class, deselect it (back function)
    if (selectedClass && classItem && selectedClass.id === classItem.id) {
      setSelectedClass(null);
      setSelectedSemester(null);
      setSubjects([]);
      return;
    }

    setSelectedClass(classItem);
    setSubjects([]);
    
    // Automatically select the current semester based on class start date
    if (classItem && classItem.startDate) {
      const classSemesters = getSemestersByClass(classItem);
      
      // Calculate current semester using the helper
      const currentSem = getCurrentSemester(classItem.startDate);
      
      if (currentSem && currentSem.semesterNumber) {
        // Find the matching semester from the backend semesters list
        const matchingSemester = classSemesters.find(sem => {
          // Try to match by semester number in the name (e.g., "Semester 1", "S1", etc.)
          const semesterNum = parseInt(sem.name?.match(/\d+/)?.[0] || '0');
          return semesterNum === currentSem.semesterNumber;
        });
        
        if (matchingSemester) {
          setSelectedSemester(matchingSemester);
        } else if (classSemesters.length > 0) {
          // Fallback: select first available semester if no match
          setSelectedSemester(classSemesters[0]);
        } else {
          setSelectedSemester(null);
        }
      } else if (classSemesters.length > 0) {
        // If no current semester calculated, select the first one
        setSelectedSemester(classSemesters[0]);
      } else {
        setSelectedSemester(null);
      }
    } else {
      setSelectedSemester(null);
    }
  };

  // Deep link from the rejection notice: /grades?semesterId=..&classId=.. opens that semester
  // directly so the learner lands on the card they have to correct.
  useEffect(() => {
    if (deepLinkApplied.current) return;
    const semesterIdParam = Number(searchParams.get("semesterId"));
    if (!semesterIdParam) return;
    if (userClasses.length === 0 || semesters.length === 0) return;

    const classIdParam = Number(searchParams.get("classId"));
    const targetClass =
      userClasses.find((item) => item.id === classIdParam) || userClasses[0];
    const targetSemester = (targetClass ? getSemestersByClass(targetClass) : semesters).find(
      (semester) => semester.id === semesterIdParam
    );

    if (targetClass && targetSemester) {
      deepLinkApplied.current = true;
      setSelectedClass(targetClass);
      setSelectedSemester(targetSemester);
      setSubjects([]);
    }
  }, [searchParams, userClasses, semesters, getSemestersByClass]);

  // Load all grades for overall chart
  const loadAllGrades = useCallback(async () => {
    if (!user) {
      setAllGrades([]);
      setAllSubjects([]);
      return;
    }

    try {
      // Get all grades for the user
      const allGradesResponse = await gradeService.getGradesByUser(user.id);
      setAllGrades(allGradesResponse || []);

      // Get all unique subject IDs from grades
      const subjectIds = [...new Set(
        (allGradesResponse || []).map(grade => grade.subjectId).filter(Boolean)
      )];

      // Fetch subject details for each unique subject
      const subjectsPromises = subjectIds.map(async (subjectId) => {
        try {
          // Try to get subject from enrollments first
          const allEnrollments = await enrollmentService.getSubjects();
          return allEnrollments.find(s => s.id === subjectId);
        } catch (error) {
          console.warn(`Could not fetch subject ${subjectId}:`, error);
          return null;
        }
      });

      const subjectsResults = await Promise.all(subjectsPromises);
      const validSubjects = subjectsResults.filter(Boolean);
      setAllSubjects(validSubjects);
    } catch (error) {
      console.error("Error loading all grades:", error);
      setAllGrades([]);
      setAllSubjects([]);
    }
  }, [user]);

  // Load grades for the selected semester
  const loadGradesForSemester = useCallback(
    async (semesterId) => {
      if (!semesterId || !user) {
        setGrades([]);
        return;
      }

      try {
        const gradesResponse = await gradeService.getMyGrades(semesterId);
        setGrades(gradesResponse || []);
      } catch (error) {
        console.error("Error loading grades:", error);
        setGrades([]);
      }
    },
    [user]
  );

  const loadSemesterConfirmations = useCallback(async () => {
    if (!selectedClass || !user) {
      setSemesterConfirmations({});
      return;
    }

    const classSemesters = getSemestersByClass(selectedClass) || [];
    if (classSemesters.length === 0) {
      setSemesterConfirmations({});
      return;
    }

    try {
      setConfirmationLoading(true);
      const semesterIds = classSemesters.map((semester) => semester.id).filter(Boolean);
      const statuses = await gradeService.getSemesterConfirmations(semesterIds, user.id, selectedClass.id);

      const mapped = (statuses || []).reduce((acc, item) => {
        const key = getConfirmationKey(item.classId ?? selectedClass.id, item.semesterId, item.learnerId ?? user.id);
        acc[key] = item;
        return acc;
      }, {});

      setSemesterConfirmations(mapped);
    } catch (error) {
      console.error("Error loading semester confirmations:", error);
    } finally {
      setConfirmationLoading(false);
    }
  }, [selectedClass, user, getSemestersByClass]);

  const loadSemesterDefinitiveRows = useCallback(async () => {
    if (!selectedSemester?.id || !user?.id) {
      setSemesterDefinitiveRows([]);
      return;
    }

    try {
      const rows = await gradeService.getSemesterDefinitiveGrades(selectedSemester.id, user.id, selectedClass?.id);
      setSemesterDefinitiveRows(rows || []);
    } catch (error) {
      console.error("Error loading definitive semester grades:", error);
      setSemesterDefinitiveRows([]);
    }
  }, [selectedClass, selectedSemester, user]);

  // Load subjects
  const loadSubjects = useCallback(async () => {
    if (!selectedClass || !selectedSemester) {
      return;
    }

    try {
      // Get user's enrolled subjects for the selected semester
      const enrollmentData = await enrollmentService.getEnrollments(
        selectedSemester.id
      );

      // Store enrollment data for later deletion
      setEnrollments(enrollmentData);

      const enrolledSubjects = enrollmentData.map(
        (enrollment) => {
          return enrollment.subject || {
            id: enrollment.subjectId || enrollment.subject_id,
            name: enrollment.subject?.name || "Unknown Subject",
            description: enrollment.subject?.description || "",
            subjectCode:
              enrollment.subject?.subjectCode ||
              enrollment.subject?.subject_code ||
              "",
            track: enrollment.subject?.track || "",
            year: enrollment.subject?.year || 1,
          };
        }
      );

      setSubjects(enrolledSubjects);
    } catch (error) {
      console.error("❌ DEBUG: Error loading subjects:", error);
      console.error("  - Status:", error.response?.status);
      console.error("  - Data:", error.response?.data);
      
      // Check if it's an auth error
      if (error.response && error.response.status === 401) {
        console.warn("Authentication expired, showing empty subjects");
        setSubjects([]);
      } else {
        setError(
          `Failed to load subjects: ${error.message || "Unknown error"}`
        );
      }
    }
  }, [selectedClass, selectedSemester]);

  // Effects
  useEffect(() => {
    
    if (user && isAuthenticated) {
      loadInitialData();
      loadAllGrades(); // Load all grades for overall chart
    } else {
    }
  }, [user, isAuthenticated, loadInitialData, loadAllGrades]);

  // Combined effect to load data when semester changes
  useEffect(() => {
    if (selectedClass && selectedSemester && user && isAuthenticated) {
      const loadData = async () => {
        setLoading(true);
        try {
          await Promise.all([
            loadSubjects(),
            loadGradesForSemester(selectedSemester.id)
          ]);
        } catch (err) {
          console.error("Error loading semester data:", err);
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    }
  }, [selectedClass, selectedSemester, user, isAuthenticated, loadSubjects, loadGradesForSemester]);

  useEffect(() => {
    if (selectedClass && user && isAuthenticated) {
      loadSemesterConfirmations();
    }
  }, [selectedClass, user, isAuthenticated, loadSemesterConfirmations]);

  useEffect(() => {
    if (selectedSemester && user && isAuthenticated) {
      loadSemesterDefinitiveRows();
    }
  }, [selectedSemester, user, isAuthenticated, loadSemesterDefinitiveRows]);

  // Grade helper functions
  const getSubjectGrades = (subjectId) => {
    return grades.filter((grade) => grade.subjectId === subjectId);
  };

  const calculateSubjectAverage = (subjectId) => {
    const subjectGrades = getSubjectGrades(subjectId);
    if (subjectGrades.length === 0) return null;

    let totalWeightedScore = 0;
    let totalWeight = 0;

    subjectGrades.forEach((grade) => {
      const weight = parseFloat(grade.weight) || 1.0;
      totalWeightedScore += parseFloat(grade.gradeValue) * weight;
      totalWeight += weight;
    });

    return totalWeight > 0
      ? roundToTwoDecimals(totalWeightedScore / totalWeight)
      : null;
  };

  // Grade modal handlers
  const handleAddGrade = async (subjectId) => {
    const subject = subjects.find((s) => s.id === subjectId);
    setSelectedSubjectForGrade(subject);
    setNewGradeData({
      gradeValue: "",
      weight: 1.0,
      description: "",
      examDate: new Date().toISOString().split("T")[0],
    });
    setShowAddGradeModal(true);
  };

  const handleSaveGrade = async () => {
    try {
      setLoading(true);
      setError("");

      if (!selectedSubjectForGrade || !selectedSemester || !user) {
        throw new Error("Missing required information for saving grade");
      }

      if (
        !newGradeData.gradeValue ||
        isNaN(parseFloat(newGradeData.gradeValue))
      ) {
        throw new Error("Please enter a valid grade value");
      }

      const gradeValue = parseFloat(newGradeData.gradeValue);
      if (gradeValue < 1 || gradeValue > 6) {
        throw new Error("Grade must be between 1.0 and 6.0");
      }

      // Create semester code for backward compatibility
      const semesterCode = selectedSemester.name || `S${selectedSemester.id}`;
      const academicYear =
        selectedSemester.academicYear || new Date().getFullYear();

      // Debug authentication before creating grade
      // const token = localStorage.getItem("authToken");

      const gradeToCreate = {
        userId: user.id,
        subjectId: selectedSubjectForGrade.id,
        gradeValue: gradeValue,
        weight: parseFloat(newGradeData.weight) || 1.0,
        description: newGradeData.description || "",
        examDate: newGradeData.examDate, // Backend expects YYYY-MM-DD format
        semesterId: selectedSemester.id, // Use actual semester ID for proper isolation
        semester: semesterCode, // Keep for backward compatibility
        academicYear: academicYear, // Keep for backward compatibility
      };

      // Create the grade via API
      const createdGrade = await gradeService.createGrade(gradeToCreate);

      if (createdGrade) {
        // Add the new grade to the local state
        setGrades((prevGrades) => [...prevGrades, createdGrade]);
        await loadSemesterConfirmations();

        // Close modal and reset form
        handleCloseGradeModal();
        setError(""); // Clear any previous errors
      }
    } catch (error) {
      console.error("Error saving grade:", error);
      setError(error.message || "Failed to save grade");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGradeModal = () => {
    setShowAddGradeModal(false);
    setSelectedSubjectForGrade(null);
    setNewGradeData({
      gradeValue: "",
      weight: 1.0,
      description: "",
      examDate: new Date().toISOString().split("T")[0],
    });
    setError("");
  };

  // Edit Grade Functions
  const handleEditGrade = (grade) => {
    setGradeToEdit(grade);
    setEditGradeData({
      gradeValue: grade.gradeValue.toString(),
      weight: (grade.weight || 1.0).toString(),
      description: grade.description || "",
      examDate: grade.examDate || "",
    });
    setShowEditGradeModal(true);
  };

  const handleSaveEditGrade = async () => {
    try {
      setLoading(true);
      setError("");

      if (!gradeToEdit) {
        throw new Error("No grade selected for editing");
      }

      const gradeValue = parseFloat(editGradeData.gradeValue);
      if (isNaN(gradeValue) || gradeValue < 1 || gradeValue > 6) {
        throw new Error("Grade must be between 1.0 and 6.0");
      }

      // Validate exam date if provided
      if (editGradeData.examDate) {
        const examDate = new Date(editGradeData.examDate);
        if (isNaN(examDate.getTime())) {
          setError("Please enter a valid exam date");
          return;
        }
      }

      const updatedGradeData = {
        ...gradeToEdit,
        gradeValue: gradeValue,
        weight: parseFloat(editGradeData.weight) || 1.0,
        description: editGradeData.description || "",
        examDate: editGradeData.examDate,
      };

      // Update the grade via API
      const updatedGrade = await gradeService.updateGrade(
        gradeToEdit.id,
        updatedGradeData
      );

      if (updatedGrade) {
        // Update local state
        setGrades((prevGrades) =>
          prevGrades.map((grade) =>
            grade.id === gradeToEdit.id ? updatedGrade : grade
          )
        );
        await loadSemesterConfirmations();

        // Close modal and reset state
        handleCloseEditGradeModal();
        setError(""); // Clear any previous errors
      }
    } catch (error) {
      console.error("Error updating grade:", error);
      setError(error.message || "Failed to update grade");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditGradeModal = () => {
    setShowEditGradeModal(false);
    setGradeToEdit(null);
    setEditGradeData({
      gradeValue: "",
      weight: 1.0,
      description: "",
      examDate: "",
    });
    setError("");
  };

  // Delete Grade Functions
  const handleDeleteGrade = (grade) => {
    setGradeToDelete(grade);
    setShowDeleteGradeModal(true);
  };

  const handleConfirmDeleteGrade = async () => {
    try {
      if (!gradeToDelete) {
        setError("No grade selected for deletion");
        return;
      }

      setLoading(true);

      // Delete the grade via API
      await gradeService.deleteGrade(gradeToDelete.id);

      // Update local state to remove the deleted grade
      setGrades((prevGrades) =>
        prevGrades.filter((grade) => grade.id !== gradeToDelete.id)
      );
      await loadSemesterConfirmations();

      // Close modal and reset state
      setShowDeleteGradeModal(false);
      setGradeToDelete(null);
      setError(""); // Clear any existing errors
    } catch (error) {
      console.error("Error deleting grade:", error);
      setError(error.message || "Failed to delete grade");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteGrade = () => {
    setShowDeleteGradeModal(false);
    setGradeToDelete(null);
  };

  // Subject Selection Modal Functions
  const handleOpenSubjectSelection = async () => {
    if (!selectedClass) {
      setError("Please select a class first");
      return;
    }

    if (!selectedSemester) {
      setError("Please select a semester first");
      return;
    }

    if (!user || !isAuthenticated) {
      setError("You must be logged in to manage subjects");
      return;
    }

    try {
      setLoading(true);

      // Get all available subjects
      const allSubjects = await enrollmentService.getSubjects();
      setAvailableSubjects(allSubjects);

      // Get current enrollments
      try {
        const enrollments = await enrollmentService.getEnrollments(
          selectedSemester.id
        );
        const currentSubjectIds = enrollments.map(
          (e) => e.subjectId || e.subject_id
        );

        // Filter out subjects already enrolled
        const availableSubjectsFiltered = allSubjects.filter(
          (subject) => !currentSubjectIds.includes(subject.id)
        );

        setAvailableSubjects(availableSubjectsFiltered);
        setSelectedSubjects(new Set());
        setSubjectFilters(getDefaultSubjectFilters(selectedClass?.professionType));
        setSubjectSearchTerm("");
        setShowSubjectSelectionModal(true);
      } catch (enrollmentError) {
        console.warn("Could not load current enrollments:", enrollmentError);
        // Still show all subjects if enrollment loading fails
        setAvailableSubjects(allSubjects);
        setSelectedSubjects(new Set());
        setSubjectFilters(getDefaultSubjectFilters(selectedClass?.professionType));
        setSubjectSearchTerm("");
        setShowSubjectSelectionModal(true);
      }
    } catch (error) {
      console.error("Error loading available subjects:", error);
      setError(error.message || "Failed to load available subjects");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSubjectSelection = () => {
    setShowSubjectSelectionModal(false);
    setSelectedSubjects(new Set());
    setSubjectFilters(getDefaultSubjectFilters(selectedClass?.professionType));
    setSubjectSearchTerm("");
  };

  const handleSubjectToggle = (subjectId) => {
    const newSelected = new Set(selectedSubjects);
    if (newSelected.has(subjectId)) {
      newSelected.delete(subjectId);
    } else {
      newSelected.add(subjectId);
    }
    setSelectedSubjects(newSelected);
  };

  const handleSaveSubjectSelection = async () => {
    
    if (!user || !isAuthenticated) {
      setError("Please log in to save subject selection");
      return;
    }

    try {
      setLoading(true);

      // Validate at least one subject is selected
      if (selectedSubjects.size === 0) {
        setError("Please select at least one subject");
        return;
      }

      // Get currently enrolled subject IDs
      const currentEnrollments = await enrollmentService.getEnrollments(selectedSemester.id);
      const currentSubjectIds = currentEnrollments.map(e => e.subjectId || e.subject_id);
      
      // Combine current + newly selected subjects
      // Convert all IDs to numbers to ensure backend compatibility
      const allSubjectIds = [...new Set([...currentSubjectIds, ...Array.from(selectedSubjects)])].map(id => Number(id));
      
      await enrollmentService.updateEnrollmentsBulk(
        selectedSemester.id,
        allSubjectIds
      );

      // Refresh subjects and close modal
      await loadSubjects();
      handleCloseSubjectSelection();
      setError(""); // Clear any existing errors
    } catch (error) {
      console.error("Error saving subject selection:", error);

      // Handle specific error cases
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 403) {
          setError(
            "Access denied. You may not have the required learner role to manage subjects."
          );
        } else if (status === 401) {
          setError(
            "Authentication expired. Please refresh the page and try again."
          );
        } else if (status === 400 && data?.message) {
          setError(`Invalid request: ${data.message}`);
        } else {
          setError(
            `Server error (${status}): ${data?.message || "Unknown error"}`
          );
        }
      } else if (error.code === "NETWORK_ERROR") {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to save subject selection. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Remove Subject Functions
  const handleRemoveSubject = (subject) => {
    setSubjectToRemove(subject);
    setShowRemoveSubjectModal(true);
  };

  const handleConfirmRemoveSubject = async () => {
    if (!subjectToRemove || !selectedSemester || !user) {
      setError("Cannot remove subject: missing required information");
      return;
    }

    try {
      setLoading(true);
      // Backend supports a bulk replace endpoint for learner enrollments
      // Build the new subjectIds array excluding the removed subject
      const currentSubjectIds = enrollments
        .map((e) => e.subject?.id || e.subjectId || e.subject_id)
        .filter(Boolean);

      const updatedSubjectIds = currentSubjectIds.filter(
        (id) => id !== subjectToRemove.id
      ).map(id => Number(id)); // Convert to numbers for backend compatibility

      // Check if this would be the last subject
      if (updatedSubjectIds.length === 0) {
        setError(
          "Cannot remove the last subject from the semester. At least one subject must remain enrolled."
        );
        setShowRemoveSubjectModal(false);
        setSubjectToRemove(null);
        setLoading(false);
        return;
      }

      // Call bulk update for the authenticated learner
      await enrollmentService.updateEnrollmentsBulk(
        selectedSemester.id,
        updatedSubjectIds
      );

      // Update local state to reflect server-side change
      setSubjects((prevSubjects) =>
        prevSubjects.filter((s) => s.id !== subjectToRemove.id)
      );
      setEnrollments((prevEnrollments) =>
        prevEnrollments.filter(
          (e) =>
            (e.subject?.id || e.subjectId || e.subject_id) !==
            subjectToRemove.id
        )
      );

      // Remove associated grades from local state
      setGrades((prevGrades) =>
        prevGrades.filter(
          (g) =>
            g.subjectId !== subjectToRemove.id &&
            g.subject_id !== subjectToRemove.id
        )
      );

      // Close modal
      setShowRemoveSubjectModal(false);
      setSubjectToRemove(null);

      setError(""); // Clear any errors
    } catch (error) {
      console.error("Error removing subject:", error);

      if (error.response) {
        const status = error.response.status;
        if (status === 403) {
          setError(
            "Access denied. You may not have permission to modify enrollments."
          );
        } else if (status === 401) {
          setError(
            "Authentication expired. Please refresh the page and try again."
          );
        } else {
          setError(
            `Failed to remove subject: ${
              error.response?.data?.message || "Server error"
            }`
          );
        }
      } else {
        setError("Failed to remove subject. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRemoveSubject = () => {
    setShowRemoveSubjectModal(false);
    setSubjectToRemove(null);
  };

  // Step 1: confirm the definitive grades. The Zeugnis is uploaded afterwards -
  // the backend rejects report uploads for semesters that are not confirmed yet.
  const handleConfirmSemester = async () => {
    if (!selectedSemester || !selectedSemester.id) {
      setError("Please select a semester to confirm.");
      return;
    }

    try {
      setConfirmationLoading(true);
      setError("");
      setConfirmationSuccess("");

      const status = await gradeService.confirmSemesterGrade(selectedSemester.id, {
        classId: selectedClass?.id,
        signatureData: "CONFIRMED",
        signedName: null,
      });

      setSemesterConfirmations((prev) => ({
        ...prev,
        [getConfirmationKey(status?.classId ?? selectedClass.id, status?.semesterId ?? selectedSemester.id, status?.learnerId ?? user.id)]: status,
      }));

      setConfirmationSuccess(
        "Grades confirmed. Please upload your Report / Zeugnis now.",
      );
      setTimeout(() => setConfirmationSuccess(""), 3500);
    } catch (confirmError) {
      console.error("Error confirming semester grade:", confirmError);
      setError(
        getErrorMessage(
          confirmError,
          "Failed to confirm semester grade.",
        ),
      );
    } finally {
      setConfirmationLoading(false);
    }
  };

  // Step 2: attach the report to the semester that was just confirmed.
  const handleUploadSemesterReport = async (reportFiles) => {
    if (!selectedSemester?.id || !reportFiles?.length) return;

    try {
      setConfirmationLoading(true);
      setError("");
      setConfirmationSuccess("");

      for (const fileObj of reportFiles) {
        await reportService.uploadReport(fileObj.fileObj, selectedSemester.id);
      }

      await loadSemesterConfirmations();
      setConfirmationSuccess("Report uploaded successfully.");
      setTimeout(() => setConfirmationSuccess(""), 3500);
    } catch (uploadError) {
      console.error("Error uploading semester report:", uploadError);
      setError(getErrorMessage(uploadError, "Failed to upload the report."));
      throw uploadError;
    } finally {
      setConfirmationLoading(false);
    }
  };

  const handleSaveDefinitiveGrades = async (subjects) => {
    if (!selectedSemester?.id || !user?.id) {
      setError("Missing learner or semester context for definitive grades.");
      return;
    }

    try {
      setConfirmationLoading(true);
      setError("");

      const payload = subjects.map((row) => ({
        subjectId: row.subjectId,
        definitiveGrade: Number(row.definitiveGrade),
      }));

      const rows = await gradeService.updateSemesterDefinitiveGrades(selectedSemester.id, user.id, selectedClass?.id, payload);
      setSemesterDefinitiveRows(rows || []);

      await loadSemesterConfirmations();

      setConfirmationSuccess("Definitive semester grades saved.");
      setTimeout(() => setConfirmationSuccess(""), 3500);
    } catch (saveError) {
      console.error("Error saving definitive semester grades:", saveError);
      setError(
        getErrorMessage(
          saveError,
          "Failed to save definitive semester grades.",
        ),
      );
    } finally {
      setConfirmationLoading(false);
    }
  };

  // Tooltip functions
  const handleGradeMouseEnter = (grade, event) => {
    setHoveredGrade(grade);
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleGradeMouseLeave = () => {
    setHoveredGrade(null);
  };

  const handleGradeMouseMove = (event) => {
    if (hoveredGrade) {
      setTooltipPosition({
        x: event.clientX,
        y: event.clientY,
      });
    }
  };

  // Subject selection helper functions
  const handleFilterToggle = (filter) => {
    setSubjectFilters((prev) => {
      if (prev.includes(filter)) {
        return prev.filter((value) => value !== filter);
      }
      return [...prev, filter];
    });
  };

  const handleSearchChange = (search) => {
    setSubjectSearchTerm(search);
  };

  const handleClearFilters = () => {
    setSubjectFilters(getDefaultSubjectFilters(selectedClass?.professionType));
    setSubjectSearchTerm("");
  };

  const getRoleLabel = useCallback((role) => {
    if (typeof role === "string") return role;
    if (role?.name) return role.name;
    if (role?.authority) return role.authority;
    return "";
  }, []);

  const normalizeRoleName = useCallback((role) => {
    const raw = getRoleLabel(role);
    if (!raw || typeof raw !== "string") return "";
    return raw.toUpperCase().replace(/^ROLE_/, "");
  }, [getRoleLabel]);

  const userRoles = useMemo(() => user?.roles || [], [user]);

  const normalizedRoles = useMemo(
    () => userRoles.map((role) => normalizeRoleName(role)).filter(Boolean),
    [userRoles, normalizeRoleName]
  );

  const canEditDefinitiveGrades = useMemo(() => {
    const editableRoles = [
      "ADMIN",
      "BERUFSBILDNER",
      "PRAXISBILDNER",
      "PRACTICE_INSTRUCTOR",
      "PRACTICAL_INSTRUCTOR",
      "LERNENDE",
      "STUDENT",
    ];
    return normalizedRoles.some((role) => editableRoles.includes(role));
  }, [normalizedRoles]);

  useEffect(() => {
  }, [
    userRoles,
    normalizedRoles,
    canEditDefinitiveGrades,
    selectedSemester,
    semesterConfirmations,
    getRoleLabel,
  ]);

  // Authentication check
  if (!isAuthenticated && !loading) {
    return (
      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="fas fa-lock fa-3x text-warning mb-3"></i>
                <h3 className="h4 mb-3">Authentication Required</h3>
                <p className="text-muted mb-4">
                  Please log in to view your grades and academic information.
                </p>
                <a href="/login" className="btn btn-primary">
                  <i className="fas fa-sign-in-alt me-2"></i>
                  Go to Login
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - only block if we have no core data
  if (loading && (!userClasses || userClasses.length === 0) && !error) {
    return <LoadingSpinner message="Loading grades..." />;
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-sunrise-red/10 border border-sunrise-red/30 text-sunrise-red px-4 py-3 rounded-lg mb-6 flex items-center">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {confirmationSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 flex items-center">
            <i className="fas fa-check-circle mr-2"></i>
            {confirmationSuccess}
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-graduation-cap text-sunrise-red mr-4"></i>
                  My Grades
                </h1>
                <p className="text-sm md:text-base text-slate-cloud">
                  View and track your academic performance
                </p>
                
                <div className="flex gap-2 mt-4">
                  <button 
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'overview' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}
                    onClick={() => setActiveTab('overview')}
                  >
                    <i className="fas fa-list mr-2"></i> Overview
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'reports' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}
                    onClick={() => setActiveTab('reports')}
                  >
                    <i className="fas fa-file-pdf mr-2"></i> Reports
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'analytics' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}
                    onClick={() => setActiveTab('analytics')}
                  >
                    <i className="fas fa-chart-line mr-2"></i> Analytics
                  </button>
                </div>
              </div>
              
              {/* Current Semester Badge */}
              {selectedClass && currentSemester && (
                <div className="mt-4 lg:mt-0 lg:ml-6">
                  <CurrentSemesterBadge 
                    classStartDate={selectedClass.startDate} 
                    professionType={selectedClass.professionType}
                    showProgress={true}
                    size="md"
                  />
                </div>
              )}
            </div>

            {/* Semester Timeline */}
            {selectedClass && calculatedSemesters.length > 0 && activeTab === 'overview' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <SemesterTimeline 
                  classStartDate={selectedClass.startDate}
                  professionType={selectedClass.professionType}
                />
              </div>
            )}
          </div>
        </div>

        {activeTab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : activeTab === 'reports' ? (
          <ReportsList isOwnProfile={true} learnerId={user?.id} />
        ) : (
          <>
        {/* Class Selection */}
        <ClassSelection
          userClasses={userClasses}
          selectedClass={selectedClass}
          onClassSelect={handleClassSelect}
        />

        {/* Semester Tabs and Class Details */}
        {selectedClass && (
          <SemesterTabs
            semesters={getSemestersByClass(selectedClass)}
            selectedSemester={selectedSemester}
            onSemesterSelect={(semester) => {
              setSelectedSemester(semester);
            }}
            selectedClass={selectedClass}
          />
        )}

        {/* Subjects Grid */}
        {selectedClass && selectedSemester && (
          loading ? (
            <div className="py-12 flex flex-col justify-center items-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-sunrise-red mb-4"></div>
              <p className="text-gray-600 font-medium">Loading class data...</p>
            </div>
          ) : (
            <SubjectsGrid
              selectedClass={selectedClass}
              selectedSemester={selectedSemester}
              subjects={subjects}
              grades={grades}
              isAuthenticated={isAuthenticated}
              user={user}
              onAddGrade={handleAddGrade}
              onEditGrade={handleEditGrade}
              onDeleteGrade={handleDeleteGrade}
              onRemoveSubject={handleRemoveSubject}
              onOpenSubjectSelection={handleOpenSubjectSelection}
              onGradeMouseEnter={handleGradeMouseEnter}
              onGradeMouseLeave={handleGradeMouseLeave}
              onGradeMouseMove={handleGradeMouseMove}
              calculateSubjectAverage={calculateSubjectAverage}
              getSubjectGrades={getSubjectGrades}
            />
          )
        )}

        {selectedClass && selectedSemester && activeTab === 'overview' && (
          <SemesterConfirmationCard
            selectedSemester={selectedSemester}
            selectedClass={selectedClass}
            confirmation={semesterConfirmations[getConfirmationKey(selectedClass?.id, selectedSemester?.id, user?.id)]}
            semesterSubjects={semesterDefinitiveRows}
            canEditDefinitiveGrades={canEditDefinitiveGrades}
            isLearner={normalizedRoles.includes("LERNENDE") || normalizedRoles.includes("STUDENT")}
            onSaveDefinitiveGrades={handleSaveDefinitiveGrades}
            onConfirm={handleConfirmSemester}
            onUploadReport={handleUploadSemesterReport}
            loading={confirmationLoading}
            debugInfo={{
              rawRoles: userRoles.map(getRoleLabel).filter(Boolean),
              normalizedRoles,
              canEditDefinitiveGrades,
              confirmationStatus: semesterConfirmations[getConfirmationKey(selectedClass?.id, selectedSemester?.id, user?.id)]?.status || "NOT_CONFIRMED",
              learnerId: user?.id,
            }}
          />
        )}

         {/* Grades Chart - Show when semester is selected and has grades */}
        {selectedClass && selectedSemester && !loading && grades.length > 0 && (
          <GradesChart
            grades={grades}
            subjects={subjects}
            selectedSemester={selectedSemester}
            calculateSubjectAverage={calculateSubjectAverage}
          />
        )}

        {/* Overall Performance Chart - Show when no semester is selected but has grades */}
        {!selectedSemester && allGrades.length > 0 && (
          <div className="mb-8">
            <div className="card-brand-elevated mb-2">
              <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center">
                      <i className="fas fa-chart-line mr-3"></i>
                      Overall Grade Performance - All Semesters
                    </h2>
                    <p className="text-sm opacity-90 mt-1">
                      <i className="fas fa-graduation-cap mr-1"></i>
                      Complete overview of your academic progress
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <GradesChart
              grades={allGrades}
              subjects={allSubjects}
              selectedSemester={null}
              calculateSubjectAverage={(subjectId) => {
                const subjectGrades = allGrades.filter(grade => grade.subjectId === subjectId);
                if (subjectGrades.length === 0) return null;

                let totalWeightedScore = 0;
                let totalWeight = 0;

                subjectGrades.forEach((grade) => {
                  const weight = parseFloat(grade.weight) || 1.0;
                  totalWeightedScore += parseFloat(grade.gradeValue) * weight;
                  totalWeight += weight;
                });

                return totalWeight > 0
                  ? roundToTwoDecimals(totalWeightedScore / totalWeight)
                  : null;
              }}
            />
          </div>
        )}
          </>
        )}

        {/* Modals */}
        <AddGradeModal
          show={showAddGradeModal}
          selectedSubject={selectedSubjectForGrade}
          gradeData={newGradeData}
          onGradeDataChange={setNewGradeData}
          onSave={handleSaveGrade}
          onClose={handleCloseGradeModal}
          error={error}
          loading={loading}
        />

        <EditGradeModal
          show={showEditGradeModal}
          grade={gradeToEdit}
          gradeData={editGradeData}
          onGradeDataChange={setEditGradeData}
          onSave={handleSaveEditGrade}
          onClose={handleCloseEditGradeModal}
          error={error}
          loading={loading}
        />

        <DeleteGradeModal
          show={showDeleteGradeModal}
          grade={gradeToDelete}
          onConfirm={handleConfirmDeleteGrade}
          onCancel={handleCancelDeleteGrade}
          loading={loading}
        />

        <SubjectSelectionModal
          show={showSubjectSelectionModal}
          selectedSemester={selectedSemester}
          availableSubjects={availableSubjects}
          selectedSubjects={selectedSubjects}
          subjectFilters={subjectFilters}
          subjectSearchTerm={subjectSearchTerm}
          loading={loading}
          onClose={handleCloseSubjectSelection}
          onSave={handleSaveSubjectSelection}
          onSubjectToggle={handleSubjectToggle}
          onFilterToggle={handleFilterToggle}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
        />

        <RemoveSubjectModal
          show={showRemoveSubjectModal}
          subject={subjectToRemove}
          selectedSemester={selectedSemester}
          loading={loading}
          onConfirm={handleConfirmRemoveSubject}
          onCancel={handleCancelRemoveSubject}
        />

        {/* Grade Tooltip */}
        <GradeTooltip
          grade={hoveredGrade}
          position={tooltipPosition}
          visible={!!hoveredGrade}
        />
      </div>
    </div>
  );
};

export default StudentGrades;
